/**
 * Nest several variables in one pass (M4S-5605). Spec: docs/pdlc/crosstabs-nested-variables.md.
 *
 * A nest crosses two or three layers (a question, Survey / Wave, or an existing bucket-based
 * variable) and makes every combination its own column. It is saved as ONE ordinary `kind:'logic'`
 * custom variable whose buckets are the combinations, so compute does not change: a column counts
 * a respondent who matches one member from EVERY layer (AND across layers), and a member counts
 * anyone matching any of its own buckets (OR within a member).
 *
 * This module is pure (no React, no fetch) so the generator can be tested against an independent
 * oracle through the public `computeFromApi` (nest-oracle.test.ts). It never imports from
 * `src/@crosstab`.
 *
 * Buckets are always the truth. The recipe (`CustomVariable.nest`) exists only so the Nest tab can
 * reopen a nest with the same ticks, labels, left-out columns and column names, and the tab opens a
 * variable only when the recipe still generates exactly its buckets (`nestMatchesBuckets`).
 *
 * A column's label is its members' labels joined with " - ", unless the researcher named that column
 * (M4S-5664, docs/pdlc/crosstabs-nest-column-rename.md). The name is kept in the recipe under the
 * column's member keys, exactly as `dropped` keeps a left-out column, so `combinations` and with
 * them `nestToBuckets` read it back: compute and every export see an ordinary bucket label.
 */

import type {
    CustomVariable,
    NestColumnName,
    NestDraft,
    NestLayer,
    NestMember,
    NestRecipe,
    VariableBucket,
    VariableCondition,
} from '@/app/(private)/ai/components/Crosstabs/crosstab-types';
import {
    bucketConnectors,
    describeBucket,
    usesBetweenBucketAnd,
} from '@/app/(private)/ai/components/Crosstabs/cv-utils';

import {
    CONDITION_RULES,
    bucketGroups,
    differsUnderEarlierRules,
    groupJoinOf,
    hasNotGroup,
    isCurrentRules,
    isGrouped,
    valuesOf,
} from './conditions';

// ── Constants ────────────────────────────────────────────────────────────────

export const NEST_MAX_LAYERS = 3;
/** Combinations a nest may have before any are left out. */
export const NEST_MAX_COLUMNS = 200;
/** Above this many columns the tab warns, and does not block. */
export const NEST_WIDE_WARNING = 40;
/** Between member labels in a column label. The deck's market parser needs a dash (slide 18). */
export const NEST_LABEL_SEPARATOR = ' - ';
/** Between layer labels in a default header name. */
export const NEST_NAME_JOINER = ' × ';
export const NEST_ID_PREFIX = 'cv_nest_';
/** Why a column cannot take a name: compute merges buckets that share a label, so two columns would become one. */
export const NEST_DUPLICATE_COLUMN_NAME = 'Another column already has this name';
/** Question kinds a layer may split by. */
export const NESTABLE_KINDS: ReadonlySet<string> = new Set(['multiple_choice', 'collapsed']);
/** Option ids DANI adds for scale nets ("scale-1-2"); they are not answers, so a layer skips them. */
export const SCALE_NET_ID = /^scale-\d+-\d+$/i;

const SURVEY_SOURCE = '__SURVEY_ID';
/** Bounds on a stored recipe, so a hand-edited record cannot make the tab enumerate the world. */
const LAYER_MEMBERS_MAX = 1000;
const MEMBER_BUCKETS_MAX = 200;
const BUCKET_CONDITIONS_MAX = 200;
const OPERATORS = new Set(['eq', 'neq', 'lt', 'gt', 'lte', 'gte', 'in', 'notIn', 'between']);

export function isNestId(id: string | undefined | null): boolean {
    return typeof id === 'string' && id.startsWith(NEST_ID_PREFIX);
}

/** A default member label: the source's label without a trailing " (Net)". */
export function memberLabel(label: string): string {
    const stripped = label.replace(/\s*\(Net\)\s*$/i, '');
    return stripped.trim() ? stripped : label;
}

// ── Copies ───────────────────────────────────────────────────────────────────

/**
 * What a condition asks, beyond the fold's connector and its group: the answers of an any of / none
 * of and the ends of a range (M4S-5777, X46). Every copy carries them, or a member built from an
 * `in` or `between` condition would be read as its first value alone, and count wrong.
 */
function askedOf(c: VariableCondition): Pick<VariableCondition, 'values' | 'min' | 'max'> {
    const out: Pick<VariableCondition, 'values' | 'min' | 'max'> = {};
    if (c.operator === 'in' || c.operator === 'notIn') out.values = valuesOf(c);
    if (c.operator === 'between') {
        if (typeof c.min === 'string' && c.min.trim()) out.min = c.min;
        if (typeof c.max === 'string' && c.max.trim()) out.max = c.max;
    }
    return out;
}

/** A condition as evalBucket reads it, without keys it does not (and never an undefined key). */
function copyCondition(c: VariableCondition): VariableCondition {
    return {
        questionId: c.questionId,
        operator: c.operator,
        value: c.value,
        ...askedOf(c),
        ...(c.nextJoin ? { nextJoin: c.nextJoin } : {}),
        ...(typeof c.group === 'number' ? { group: c.group } : {}),
    };
}

/** A member's bucket. The bucket-level `nextJoin` is left behind: a member counts anyone in any bucket. */
function copyMemberBucket(b: VariableBucket): VariableBucket {
    return {
        label: b.label,
        join: b.join,
        conditions: b.conditions.map(copyCondition),
        // M4S-5777 (X46): a grouped bucket keeps its groups; without them its conditions would fold.
        ...(isGrouped(b)
            ? {
                  groups: b.groups!.map((g) =>
                      g.not ? { join: g.join, not: true as const } : { join: g.join },
                  ),
                  groupJoin: groupJoinOf(b),
              }
            : {}),
    };
}

function copyMember(m: NestMember): NestMember {
    return { key: m.key, label: m.label, buckets: m.buckets.map(copyMemberBucket) };
}

// ── Generating buckets ───────────────────────────────────────────────────────

/**
 * A bucket as OR-of-AND lists. `evalBucket` folds the conditions left to right, so the fold is
 * `((c1 op c2) op c3)…`: an OR starts a new list, and an AND extends every list so far.
 */
export function toDnf(bucket: VariableBucket): VariableCondition[][] {
    if (bucket.conditions.length === 0) return [];
    // M4S-5777 (X46): groups. An OR group is one list per condition, an AND group one list of all
    // of them; groups joined by AND multiply out, by OR add up. A NOT group has no such form (and
    // NOT (A) is not A's ≠ for someone never asked), so a variable with one is refused as a source
    // (`variableRefusal`) and its bucket gives no lists here.
    const groups = bucketGroups(bucket);
    if (groups) {
        if (hasNotGroup(bucket)) return [];
        const lists = groups.map((g) =>
            g.join === 'all'
                ? [g.conditions.map(bareCondition)]
                : g.conditions.map((c) => [bareCondition(c)]),
        );
        if (groupJoinOf(bucket) === 'any') return lists.flat();
        return lists.reduce<VariableCondition[][]>(
            (acc, l) => acc.flatMap((prefix) => l.map((list) => [...prefix, ...list])),
            [[]],
        );
    }
    let lists: VariableCondition[][] = [[bucket.conditions[0]]];
    const connectors = bucketConnectors(bucket);
    for (let i = 0; i < connectors.length; i++) {
        const next = bucket.conditions[i + 1];
        lists = connectors[i] === 'all' ? lists.map((l) => [...l, next]) : [...lists, [next]];
    }
    return lists;
}

function bareCondition(c: VariableCondition): VariableCondition {
    return { questionId: c.questionId, operator: c.operator, value: c.value, ...askedOf(c) };
}

/** Conditions with an explicit connector after each but the last, and no undefined key. */
function chained(conditions: VariableCondition[], joins: ('all' | 'any')[]): VariableCondition[] {
    return conditions.map((c, i) =>
        i < conditions.length - 1 ? { ...bareCondition(c), nextJoin: joins[i] } : bareCondition(c),
    );
}

/**
 * The buckets of ONE combination: everyone who matches one member from every layer.
 *
 * Gluing the members' conditions end to end is wrong whenever a later member holds an OR, because
 * the fold has no grouping. So: the member with the most OR-of-AND lists (the earlier layer, on a
 * tie) is the head and is kept as written; every other member is spread into its lists; and one
 * bucket is emitted per head bucket and per choice of one list from each other member. All of them
 * carry the combination's label, so compute's same-label merge counts them as one column.
 *
 * Every bucket follows one rule of its own: `join 'any'`, every connector explicit, no
 * bucket-level `nextJoin`, no `bucketJoin` and no undefined key. That keeps the generated shape
 * stable through JSONB and silences `hasDuplicateEqOnSameQuestion`. A member with no usable bucket
 * matches nobody, so the combination has no buckets at all.
 */
export function bucketsForCombination(members: NestMember[], label: string): VariableBucket[] {
    if (members.length === 0) return [];
    const lists = members.map((m) => m.buckets.flatMap(toDnf));
    if (lists.some((l) => l.length === 0)) return [];

    let headIdx = 0;
    lists.forEach((l, i) => {
        if (l.length > lists[headIdx].length) headIdx = i;
    });

    let tails: VariableCondition[][] = [[]];
    members.forEach((_, i) => {
        if (i === headIdx) return;
        tails = tails.flatMap((tail) => lists[i].map((list) => [...tail, ...list]));
    });

    const out: VariableBucket[] = [];
    // A grouped head bucket (X46) cannot be kept as written in a fold, so it is spread into its
    // OR-of-AND lists like every other member, each list a head of its own.
    const heads: { conditions: VariableCondition[]; joins: ('all' | 'any')[] }[] = members[
        headIdx
    ].buckets.flatMap((head) =>
        isGrouped(head)
            ? toDnf(head).map((list) => ({
                  conditions: list,
                  joins: list.slice(1).map(() => 'all' as const),
              }))
            : [{ conditions: head.conditions, joins: bucketConnectors(head) }],
    );
    for (const head of heads) {
        if (head.conditions.length === 0) continue;
        for (const tail of tails) {
            const conditions = [...head.conditions, ...tail];
            const joins = [...head.joins, ...tail.map(() => 'all' as const)];
            out.push({ label, join: 'any', conditions: chained(conditions, joins) });
        }
    }
    return out;
}

/**
 * One column of a nest: its member keys in layer order, its label and its members. The label is the
 * name the researcher gave the column (M4S-5664) or, when there is none, the one its members make.
 */
export interface NestCombination {
    keys: string[];
    /** What the column is called: its own name when it has one, else `generated`. */
    label: string;
    /** What its members' labels make, whatever the column is called. */
    generated: string;
    /** True when `label` is a name given to this column and not the generated one. */
    renamed: boolean;
    members: NestMember[];
}

function comboKey(keys: readonly string[]): string {
    return JSON.stringify(keys);
}

/**
 * The names given to columns, by `comboKey`. A blank name is no name, and when two entries name one
 * column the first wins, which is also the one `pruneCombos` keeps.
 */
function nameMap(names: readonly NestColumnName[] | undefined): Map<string, string> {
    const out = new Map<string, string>();
    for (const n of names ?? []) {
        const id = comboKey(n.keys);
        if (n.label.trim() && !out.has(id)) out.set(id, n.label);
    }
    return out;
}

/**
 * Every combination of one member from each layer, layer 1 outermost, members in layer order. A
 * combination whose keys have an entry in `names` takes that name; the rest are their members'
 * labels joined with NEST_LABEL_SEPARATOR.
 */
function crossLayers(
    layers: NestMember[][],
    names?: ReadonlyMap<string, string>,
): NestCombination[] {
    let acc: NestMember[][] = [[]];
    for (const members of layers) {
        acc = acc.flatMap((prefix) => members.map((m) => [...prefix, m]));
    }
    return acc.map((ms) => {
        const keys = ms.map((m) => m.key);
        const generated = ms.map((m) => m.label).join(NEST_LABEL_SEPARATOR);
        const given = names?.get(comboKey(keys));
        // A stored name that spells the generated one is not a name: the column still follows its members.
        const renamed = given !== undefined && given !== generated;
        return { keys, label: renamed ? given : generated, generated, renamed, members: ms };
    });
}

/**
 * The columns a recipe keeps, layer 1 outermost, each under its own name when it has one. Left-out
 * combinations are not in the list (their names stay in the recipe, so putting a column back
 * brings its name back).
 */
export function combinations(recipe: NestRecipe): NestCombination[] {
    const dropped = new Set((recipe.dropped ?? []).map(comboKey));
    return crossLayers(
        recipe.layers.map((l) => l.members),
        nameMap(recipe.names),
    ).filter((c) => !dropped.has(comboKey(c.keys)));
}

export function nestToBuckets(recipe: NestRecipe): VariableBucket[] {
    return combinations(recipe).flatMap((c) => bucketsForCombination(c.members, c.label));
}

// ── Reading a saved nest back ────────────────────────────────────────────────

/** What compute reads of a bucket list, and nothing else: the shape two bucket lists are compared in. */
export interface CanonicalBucket {
    label: string;
    conditions: {
        q: string;
        op: string;
        v: string;
        next: 'all' | 'any' | null;
        /** X46: an any of / none of's answers, upper-cased, in order; a range's ends. */
        vs?: string[];
        lo?: string;
        hi?: string;
        g?: number;
    }[];
    /** X46: a grouped bucket's groups and how they are joined. */
    groups?: { join: 'all' | 'any'; not: boolean }[];
    groupJoin?: 'all' | 'any';
}

/**
 * Buckets reduced to exactly what `evalBucket` reads, so two lists that count the same compare
 * equal whatever their storage. ai_layout is JSONB, which returns object keys in another order
 * and drops undefined ones, so a JSON string compare of the raw buckets is wrong.
 *  - the label;
 *  - each condition's upper-cased question id, its operator and its trimmed, upper-cased value;
 *  - the connector each condition passes to the next, `nextJoin ?? join` (none after the last).
 */
export function canonicalBuckets(buckets: VariableBucket[]): CanonicalBucket[] {
    return buckets.map((b) => {
        const grouped = isGrouped(b);
        const out: CanonicalBucket = {
            label: b.label,
            conditions: b.conditions.map((c, i) => {
                const asked = askedOf(c);
                return {
                    q: c.questionId.toUpperCase(),
                    op: c.operator,
                    v: c.value.trim().toUpperCase(),
                    // A grouped bucket does not fold, so its connectors are not read.
                    next: !grouped && i < b.conditions.length - 1 ? (c.nextJoin ?? b.join) : null,
                    ...(asked.values ? { vs: asked.values.map((v) => v.toUpperCase()) } : {}),
                    ...(asked.min ? { lo: asked.min.trim().toUpperCase() } : {}),
                    ...(asked.max ? { hi: asked.max.trim().toUpperCase() } : {}),
                    ...(grouped ? { g: typeof c.group === 'number' ? c.group : 0 } : {}),
                };
            }),
        };
        if (grouped) {
            out.groups = b.groups!.map((g) => ({
                join: g.join === 'all' ? 'all' : 'any',
                not: g.not === true,
            }));
            out.groupJoin = groupJoinOf(b);
        }
        return out;
    });
}

/**
 * True when `cv.nest` still generates exactly `cv.buckets`. False after a DANI rewrite, a Logic
 * edit or any other hand change, and whenever a between-bucket AND would intersect the columns.
 * Never throws: a variable read from JSON may be malformed.
 */
export function nestMatchesBuckets(cv: CustomVariable): boolean {
    try {
        if (cv.kind !== 'logic' || cv.bucketJoin === 'all') return false;
        if (cv.buckets.some((b) => b.nextJoin === 'all') || usesBetweenBucketAnd(cv)) return false;
        const recipe = parseNest(cv.nest);
        if (!recipe) return false;
        return (
            JSON.stringify(canonicalBuckets(nestToBuckets(recipe))) ===
            JSON.stringify(canonicalBuckets(cv.buckets))
        );
    } catch {
        return false;
    }
}

function isRecord(x: unknown): x is Record<string, unknown> {
    return typeof x === 'object' && x !== null && !Array.isArray(x);
}

function parseCondition(raw: unknown): VariableCondition | null {
    if (!isRecord(raw)) return null;
    const { questionId, operator, value, nextJoin, values, min, max, group } = raw;
    if (typeof questionId !== 'string' || typeof value !== 'string') return null;
    if (typeof operator !== 'string' || !OPERATORS.has(operator)) return null;
    if (nextJoin != null && nextJoin !== 'all' && nextJoin !== 'any') return null;
    // M4S-5777 (X46): the answers of an any of / none of, and the ends of a range.
    if (values != null && (!Array.isArray(values) || values.some((v) => typeof v !== 'string'))) {
        return null;
    }
    if ((min != null && typeof min !== 'string') || (max != null && typeof max !== 'string')) {
        return null;
    }
    if (group != null && (typeof group !== 'number' || !Number.isInteger(group) || group < 0)) {
        return null;
    }
    const parsed: VariableCondition = {
        questionId,
        operator: operator as VariableCondition['operator'],
        value,
        ...(nextJoin === 'all' || nextJoin === 'any' ? { nextJoin } : {}),
        ...(typeof group === 'number' ? { group } : {}),
    };
    return {
        ...parsed,
        ...askedOf({
            ...parsed,
            values: values as string[] | undefined,
            min: min as string | undefined,
            max: max as string | undefined,
        }),
    };
}

function parseBucket(raw: unknown): VariableBucket | null {
    if (!isRecord(raw)) return null;
    const { label, join, conditions, groups, groupJoin } = raw;
    if (typeof label !== 'string' || (join !== 'all' && join !== 'any')) return null;
    if (!Array.isArray(conditions) || conditions.length === 0) return null;
    if (conditions.length > BUCKET_CONDITIONS_MAX) return null;
    const parsed: VariableCondition[] = [];
    for (const c of conditions) {
        const p = parseCondition(c);
        if (!p) return null;
        parsed.push(p);
    }
    const out: VariableBucket = { label, join, conditions: parsed };
    // M4S-5777 (X46): explicit groups, each `{ join, not? }`.
    if (groups != null) {
        if (!Array.isArray(groups) || groups.length > BUCKET_CONDITIONS_MAX) return null;
        const gs: VariableBucket['groups'] = [];
        for (const g of groups) {
            if (!isRecord(g) || (g.join !== 'all' && g.join !== 'any')) return null;
            gs.push(g.not === true ? { join: g.join, not: true } : { join: g.join });
        }
        if (gs.length) {
            out.groups = gs;
            out.groupJoin = groupJoin === 'any' ? 'any' : 'all';
        }
    }
    return out;
}

/** A member read from JSON, or null when it is malformed. Also reads a weighting group (BT-2084). */
export function parseMember(raw: unknown): NestMember | null {
    if (!isRecord(raw)) return null;
    const { key, label, buckets } = raw;
    if (typeof key !== 'string' || !key || typeof label !== 'string') return null;
    if (!Array.isArray(buckets) || buckets.length === 0) return null;
    if (buckets.length > MEMBER_BUCKETS_MAX) return null;
    const parsed: VariableBucket[] = [];
    for (const b of buckets) {
        const p = parseBucket(b);
        if (!p) return null;
        parsed.push(p);
    }
    return { key, label, buckets: parsed };
}

function parseLayer(raw: unknown): NestLayer | null {
    if (!isRecord(raw)) return null;
    const { source, label, members } = raw;
    if (typeof source !== 'string' || !source || typeof label !== 'string') return null;
    if (!Array.isArray(members) || members.length === 0) return null;
    if (members.length > LAYER_MEMBERS_MAX) return null;
    const parsed: NestMember[] = [];
    const keys = new Set<string>();
    for (const m of members) {
        const p = parseMember(m);
        // A key names one member of a layer; two with one key would make a combination ambiguous.
        if (!p || keys.has(p.key)) return null;
        keys.add(p.key);
        parsed.push(p);
    }
    return { source, label, members: parsed };
}

/**
 * The entries that still name a column of `layers`: one key per layer, each the key of a ticked
 * member of that layer. The first of two entries naming one column is kept. This is how both
 * `dropped` and `names` are pruned, on the way in (`parseNest`) and on the way out (`draftToRecipe`),
 * so the two lists can never disagree about which columns exist.
 */
function pruneCombos<T>(
    entries: readonly T[],
    keysOf: (entry: T) => readonly string[],
    layers: readonly { members: readonly NestMember[] }[],
): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const entry of entries) {
        const keys = keysOf(entry);
        const live =
            keys.length === layers.length &&
            keys.every((k, i) => layers[i].members.some((m) => m.key === k));
        const id = comboKey(keys);
        if (live && !seen.has(id)) {
            seen.add(id);
            out.push(entry);
        }
    }
    return out;
}

function isStringList(x: unknown): x is string[] {
    return Array.isArray(x) && x.every((k) => typeof k === 'string');
}

/**
 * A stored recipe (untrusted JSON: the record, a POST body) to a well-formed one, or null when it
 * is malformed. Never throws. `dropped` and `names` entries that no longer name ticked members are
 * pruned, and so is a name that is blank; a `dropped` that is not a list of string lists, or a
 * `names` that is not a list of `{ keys: string[], label: string }`, makes the whole recipe
 * malformed (M4S-5664: `names` is handled the way `dropped` is).
 */
export function parseNest(raw: unknown): NestRecipe | null {
    if (!isRecord(raw) || raw.version !== 1) return null;
    const { layers, dropped, names } = raw;
    if (!Array.isArray(layers) || layers.length < 2 || layers.length > NEST_MAX_LAYERS) {
        return null;
    }
    const parsedLayers: NestLayer[] = [];
    for (const l of layers) {
        const p = parseLayer(l);
        if (!p) return null;
        parsedLayers.push(p);
    }
    // A stored nest is never larger than the tab allows; a bigger one was not built by the tab.
    const total = parsedLayers.reduce((n, l) => n * l.members.length, 1);
    if (total > NEST_MAX_COLUMNS) return null;

    const droppedEntries: string[][] = [];
    if (dropped != null) {
        if (!Array.isArray(dropped)) return null;
        for (const entry of dropped) {
            if (!isStringList(entry)) return null;
            droppedEntries.push(entry);
        }
    }
    const nameEntries: NestColumnName[] = [];
    if (names != null) {
        if (!Array.isArray(names)) return null;
        for (const entry of names) {
            if (!isRecord(entry) || !isStringList(entry.keys) || typeof entry.label !== 'string') {
                return null;
            }
            nameEntries.push({ keys: entry.keys, label: entry.label });
        }
    }

    const prunedDropped = pruneCombos(droppedEntries, (keys) => keys, parsedLayers).map((keys) => [
        ...keys,
    ]);
    const prunedNames = pruneCombos(
        nameEntries.filter((n) => n.label.trim() !== ''),
        (n) => n.keys,
        parsedLayers,
    ).map((n) => ({ keys: [...n.keys], label: n.label }));
    return {
        version: 1,
        layers: parsedLayers,
        ...(prunedDropped.length ? { dropped: prunedDropped } : {}),
        ...(prunedNames.length ? { names: prunedNames } : {}),
    };
}

// ── Sources: what a layer can split by ───────────────────────────────────────

/** The part of the workspace's DataCatalog that nests read (a structural subset of it). */
export interface NestCatalog {
    rowQuestions: Array<{
        id: string;
        qid: string;
        text: string;
        kind: string;
        /** Set when this question is a `<QID>__vN` variant: the column its answers are read from. */
        responseColumnId?: string;
    }>;
    bannerVarDefs: Record<string, { label: string; cats: Array<{ id: string; label: string }> }>;
    totalSurveys?: number;
}

export interface NestSource {
    /** The value a layer stores as `source`: a qid, '__SURVEY_ID', `cv:<id>` or `group:<name>`. */
    id: string;
    kind: 'question' | 'survey' | 'variable' | 'group';
    /** What a header or a column reads: the question text, 'Survey / Wave', the variable or group name. */
    label: string;
    /** What the picker shows. */
    title: string;
    /** The upper-cased response columns the source reads. */
    columns: string[];
    /** Why the source cannot be picked. Absent when it can. */
    disabled?: string;
    /** The members it offers, in programmed order. Empty on a source that is never offered. */
    members: NestMember[];
}

export interface NestSourceSection {
    key: 'questions' | 'survey' | 'variables';
    title: string;
    sources: NestSource[];
}

/** A layer as the sources check needs it: a draft layer or a saved one. */
type LayerLike = { source: string; label: string } & (
    | { options: { member: NestMember }[] }
    | { members: NestMember[] }
);

function membersOfLayer(layer: LayerLike): NestMember[] {
    return 'options' in layer ? layer.options.map((o) => o.member) : layer.members;
}

/** The distinct upper-cased question ids a list of members reads. */
function columnsOfMembers(members: NestMember[]): string[] {
    const out = new Set<string>();
    for (const m of members) {
        for (const b of m.buckets)
            for (const c of b.conditions) out.add(c.questionId.toUpperCase());
    }
    return [...out];
}

/** A member for one answer (or survey) of a question. */
function answerMember(qid: string, cat: { id: string; label: string }): NestMember {
    const label = memberLabel(cat.label);
    return {
        key: `${qid.toUpperCase()}=${cat.id}`,
        label,
        buckets: [
            {
                label,
                join: 'any',
                conditions: [{ questionId: qid, operator: 'eq', value: cat.id }],
            },
        ],
    };
}

function questionSource(q: NestCatalog['rowQuestions'][number], catalog: NestCatalog): NestSource {
    const cats = (catalog.bannerVarDefs[q.qid] ?? catalog.bannerVarDefs[q.id])?.cats ?? [];
    const answers = cats.filter((c) => !SCALE_NET_ID.test(c.id));
    const column = (q.responseColumnId || q.qid).toUpperCase();
    const label = q.text.trim() || q.qid;
    const source: NestSource = {
        id: q.qid,
        kind: 'question',
        label,
        title: `${q.qid} — ${label}`,
        columns: [column],
        members: [],
    };
    const kind = (q.kind ?? '').toLowerCase();
    if (q.responseColumnId && q.responseColumnId.toUpperCase() !== q.qid.toUpperCase()) {
        source.disabled = `Worded differently in another survey. Nest ${q.responseColumnId}: its answers are read from the same column for every survey.`;
    } else if (!NESTABLE_KINDS.has(kind)) {
        source.disabled = /matrix|grid/.test(kind)
            ? "Matrix questions can't be nested."
            : /intensity/.test(kind)
              ? "Intensity scales can't be nested."
              : /rank/.test(kind)
                ? "Ranked-order questions can't be nested."
                : /open|text/.test(kind)
                  ? "Open-ended questions can't be nested."
                  : "This kind of question can't be nested.";
    } else if (answers.length === 0) {
        source.disabled = 'It has no answers to split by.';
    }
    // A source that is never offered needs no members.
    if (!source.disabled) source.members = answers.map((c) => answerMember(q.qid, c));
    return source;
}

function surveySource(catalog: NestCatalog): NestSource | null {
    const def = catalog.bannerVarDefs[SURVEY_SOURCE];
    if (!def || (catalog.totalSurveys ?? 0) < 2) return null;
    return {
        id: SURVEY_SOURCE,
        kind: 'survey',
        label: 'Survey / Wave',
        title: 'Survey / Wave',
        columns: [SURVEY_SOURCE],
        members: def.cats.map((c) => answerMember(SURVEY_SOURCE, c)),
    };
}

/** Why a variable cannot be a member of a layer, or null when it can. */
function variableRefusal(
    cv: CustomVariable,
    variantQids: Set<string>,
    editingId: string | undefined,
): string | null {
    if (cv.id === editingId) return "It's the variable you're editing.";
    if (isNestId(cv.id) || cv.nest) return "A nest can't be a layer of another nest.";
    if (cv.kind === 'formula') return "Formula variables can't be nested.";
    if (cv.kind === 'combine') return "Combined questions can't be nested.";
    if (cv.kind === 'bucketing') return "Numeric ranges can't be nested.";
    if (cv.buckets.length === 0) return 'It has no buckets.';
    if (usesBetweenBucketAnd(cv)) {
        return 'It joins its buckets with AND, so its columns would be identical. Rebuild it without AND to nest it.';
    }
    // M4S-5777 (X46): NOT ( … ) has no form a nest's combinations can carry. Spreading it out
    // would need NOT of each answer, and NOT (A) is not A's ≠ for someone never asked.
    if (cv.buckets.some(hasNotGroup)) {
        return 'It uses NOT ( … ), which a nest cannot cross. Rebuild it without NOT to nest it.';
    }
    const conditions = cv.buckets.flatMap((b) => b.conditions);
    if (conditions.some((c) => variantQids.has(c.questionId.toUpperCase()))) {
        return 'It reads a question that is worded differently in another survey.';
    }
    // M4S-5777: a nest is saved under today's rules, and its members are copies of this
    // variable's buckets. A ≠ or a comparison saved under the earlier rules would change meaning in
    // the copy (a typed code counts no one, ≠ drops people never asked), so it is refused.
    if (!isCurrentRules(cv) && differsUnderEarlierRules(cv)) {
        return 'It was saved under the earlier condition rules. Edit and save it first.';
    }
    return null;
}

/** A variable's members: each distinct bucket label, counting anyone in any of its buckets. */
function variableMembers(cv: CustomVariable): NestMember[] {
    const labels: string[] = [];
    for (const b of cv.buckets) {
        if (b.label.trim() && !labels.includes(b.label)) labels.push(b.label);
    }
    return labels.map((label) => ({
        key: `${cv.id}#${label}`,
        label: memberLabel(label),
        buckets: cv.buckets.filter((b) => b.label === label).map(copyMemberBucket),
    }));
}

function variableSources(
    customVariables: CustomVariable[],
    variantQids: Set<string>,
    editingId: string | undefined,
): NestSource[] {
    // A variable on its own, or a group: variables sharing a `cvGroupName` are offered ONCE, under
    // that header, where the first of them sits.
    type Entry =
        | { kind: 'variable'; cv: CustomVariable }
        | { kind: 'group'; name: string; cvs: CustomVariable[] };
    const entries: Entry[] = [];
    const groups = new Map<string, Extract<Entry, { kind: 'group' }>>();
    for (const cv of customVariables) {
        const name = cv.cvGroupName?.trim();
        if (!name) {
            entries.push({ kind: 'variable', cv });
            continue;
        }
        let group = groups.get(name);
        if (!group) {
            group = { kind: 'group', name, cvs: [] };
            groups.set(name, group);
            entries.push(group);
        }
        group.cvs.push(cv);
    }

    return entries.map((entry): NestSource => {
        if (entry.kind === 'variable') {
            const { cv } = entry;
            const refusal = variableRefusal(cv, variantQids, editingId);
            const members = refusal ? [] : variableMembers(cv);
            const label = cv.name.trim() || cv.id;
            return {
                id: `cv:${cv.id}`,
                kind: 'variable',
                label,
                title: label,
                columns: columnsOfMembers(members),
                ...(refusal ? { disabled: refusal } : {}),
                members,
            };
        }
        // A group offers the variables that can be nested. A nest or a formula that joined the
        // header does not take the rest of the group away with it.
        const refusals = entry.cvs.map((cv) => variableRefusal(cv, variantQids, editingId));
        const members = entry.cvs.flatMap((cv, i) => (refusals[i] ? [] : variableMembers(cv)));
        return {
            id: `group:${entry.name}`,
            kind: 'group',
            label: entry.name,
            title: entry.name,
            columns: columnsOfMembers(members),
            ...(members.length === 0
                ? { disabled: refusals.find((r) => r !== null) ?? 'It has no buckets.' }
                : {}),
            members,
        };
    });
}

/**
 * The sources a layer can split by, in three sections (Questions, Survey / Wave, Variables), with
 * every source that cannot be picked shown and given its reason.
 *
 * One question per nest, enforced here as the researcher picks: each source knows the response
 * columns it reads, and once a layer holds one, every other source that reads one of the same
 * columns is disabled with "Already split by layer N (label)". That is what keeps the base's
 * crossed groups (Gender, Age, Income, Purchase Experience: each demo AND exposure) valid beside
 * State, and refuses them once exposure is a layer. `forLayer` is the layer the picker belongs to:
 * its own source is not counted against itself.
 */
export function nestSources(
    catalog: NestCatalog,
    customVariables: CustomVariable[],
    opts: { layers?: LayerLike[]; editingId?: string; forLayer?: number } = {},
): NestSourceSection[] {
    const variantQids = new Set(
        catalog.rowQuestions
            .filter(
                (q) =>
                    q.responseColumnId && q.responseColumnId.toUpperCase() !== q.qid.toUpperCase(),
            )
            .map((q) => q.qid.toUpperCase()),
    );

    const sections: NestSourceSection[] = [
        {
            key: 'questions',
            title: 'Questions',
            sources: catalog.rowQuestions.map((q) => questionSource(q, catalog)),
        },
    ];
    const survey = surveySource(catalog);
    if (survey) sections.push({ key: 'survey', title: 'Survey / Wave', sources: [survey] });
    const variables = variableSources(customVariables, variantQids, opts.editingId);
    if (variables.length)
        sections.push({ key: 'variables', title: 'Variables', sources: variables });

    const others = (opts.layers ?? [])
        .map((layer, i) => ({
            layer,
            i,
            columns: new Set(columnsOfMembers(membersOfLayer(layer))),
        }))
        .filter(({ i }) => i !== opts.forLayer);
    for (const section of sections) {
        for (const source of section.sources) {
            if (source.disabled) continue;
            const clash = others.find(({ columns }) => source.columns.some((c) => columns.has(c)));
            if (clash) {
                source.disabled = `Already split by layer ${clash.i + 1} (${clash.layer.label})`;
            }
        }
    }
    return sections;
}

// ── The draft the Nest tab edits ─────────────────────────────────────────────

export function emptyDraft(): NestDraft {
    return { layers: [], dropped: [], names: [] };
}

/** Adds a layer for `source`, every member ticked, answers in programmed order. */
export function addLayer(draft: NestDraft, source: NestSource): NestDraft {
    return {
        ...draft,
        layers: [
            ...draft.layers,
            {
                source: source.id,
                label: source.label,
                options: source.members.map((m) => ({ member: copyMember(m), ticked: true })),
            },
        ],
    };
}

/**
 * A saved recipe as a draft: its members ticked with their saved labels and definitions, then any
 * live member of the source the recipe does not have, unticked. A nest is a copy: it keeps the
 * definition each member had when it was saved. A source that is gone keeps its saved members.
 * Left-out columns and column names carry over. Never mutates the recipe.
 */
export function recipeToDraft(
    recipe: NestRecipe,
    sources: NestSourceSection[] | NestSource[],
): NestDraft {
    const flat: NestSource[] = (sources as (NestSourceSection | NestSource)[]).flatMap((s) =>
        'sources' in s ? s.sources : [s],
    );
    return {
        layers: recipe.layers.map((layer) => {
            // M4S-5777 (X32): a merged member covers the members it was merged from, so those are
            // not offered again unticked beside it (ticking one would count a respondent twice).
            const saved = new Set(layer.members.flatMap((m) => [m.key, ...coveredKeys(m.key)]));
            const live = flat.find((s) => s.id === layer.source)?.members ?? [];
            return {
                source: layer.source,
                label: layer.label,
                options: [
                    ...layer.members.map((m) => ({ member: copyMember(m), ticked: true })),
                    ...live
                        .filter((m) => !saved.has(m.key))
                        .map((m) => ({ member: copyMember(m), ticked: false })),
                ],
            };
        }),
        dropped: (recipe.dropped ?? []).map((keys) => [...keys]),
        names: (recipe.names ?? []).map((n) => ({ keys: [...n.keys], label: n.label })),
    };
}

/**
 * The recipe a draft saves: ticked members only, in order, with `dropped` and `names` pruned to
 * combinations that still exist (the way `parseNest` prunes them, so what is saved is what a
 * reopen reads back). A column that was left out keeps its name; one whose member is no longer
 * ticked loses it. It may hold fewer than two layers or an empty layer; `nestBlocker` decides
 * whether that can be saved.
 */
export function draftToRecipe(draft: NestDraft): NestRecipe {
    const layers: NestLayer[] = draft.layers.map((l) => ({
        source: l.source,
        label: l.label,
        members: l.options.filter((o) => o.ticked).map((o) => copyMember(o.member)),
    }));
    const dropped = pruneCombos(draft.dropped, (keys) => keys, layers).map((keys) => [...keys]);
    const names = pruneCombos(
        draft.names.filter((n) => n.label.trim() !== ''),
        (n) => n.keys,
        layers,
    ).map((n) => ({ keys: [...n.keys], label: n.label }));
    return {
        version: 1,
        layers,
        ...(dropped.length ? { dropped } : {}),
        ...(names.length ? { names } : {}),
    };
}

/** How many combinations the ticked members make, before any are left out. */
export function draftColumnCount(draft: NestDraft): number {
    if (draft.layers.length === 0) return 0;
    return draft.layers.reduce((n, l) => n * l.options.filter((o) => o.ticked).length, 1);
}

/** A combination of the ticked members, and whether the researcher left it out. */
export interface NestColumn extends NestCombination {
    dropped: boolean;
}

/**
 * Every combination of the ticked members, layer 1 outermost, each flagged when left out and each
 * under its own name when the researcher gave it one (`renamed`, with the generated name in
 * `generated`). Empty above NEST_MAX_COLUMNS: past that the tab shows a count and no list, and a
 * full product of three big layers would be millions of rows. Check `draftColumnCount` first to
 * tell the two apart.
 */
export function draftColumns(draft: NestDraft): NestColumn[] {
    const count = draftColumnCount(draft);
    if (count === 0 || count > NEST_MAX_COLUMNS) return [];
    const dropped = new Set(draft.dropped.map(comboKey));
    return crossLayers(
        draft.layers.map((l) => l.options.filter((o) => o.ticked).map((o) => o.member)),
        nameMap(draft.names),
    ).map((c) => ({ ...c, dropped: dropped.has(comboKey(c.keys)) }));
}

// ── Editing the draft (the Nest tab) ─────────────────────────────────────────
//
// Each helper returns a new draft and leaves the one it was given alone, so the modal can hold the
// draft in state and the tab can be a plain function of it. A layer is identified by its position,
// a member by its key. `dropped` lists combinations by member keys in LAYER ORDER, so anything that
// changes which layer is where has to change those lists with it, or they would leave out other
// columns than the ones the researcher chose.

function withLayer(
    draft: NestDraft,
    layerIdx: number,
    edit: (layer: NestDraft['layers'][number]) => NestDraft['layers'][number],
): NestDraft {
    if (layerIdx < 0 || layerIdx >= draft.layers.length) return draft;
    const before = draft.layers[layerIdx];
    const after = edit(before);
    // An edit that changed nothing hands back the very draft it was given.
    if (after === before || after.options.every((o, i) => o === before.options[i])) return draft;
    return { ...draft, layers: draft.layers.map((l, i) => (i === layerIdx ? after : l)) };
}

/**
 * Replaces layer `layerIdx` with `source`, every member ticked. Choosing the source it already has
 * changes nothing. Left-out columns and column names are cleared: their keys named members of the
 * old source.
 */
export function setLayerSource(draft: NestDraft, layerIdx: number, source: NestSource): NestDraft {
    const current = draft.layers[layerIdx];
    if (!current || current.source === source.id) return draft;
    return {
        layers: draft.layers.map((l, i) =>
            i === layerIdx ? addLayer(emptyDraft(), source).layers[0] : l,
        ),
        dropped: [],
        names: [],
    };
}

/**
 * Removes layer `layerIdx`. The columns left out and the columns named no longer exist (a column is
 * its members from EVERY layer, so the one that is left is a different column), so both lists are
 * cleared.
 */
export function removeLayer(draft: NestDraft, layerIdx: number): NestDraft {
    if (layerIdx < 0 || layerIdx >= draft.layers.length) return draft;
    return { layers: draft.layers.filter((_, i) => i !== layerIdx), dropped: [], names: [] };
}

/**
 * Swaps layer `layerIdx` with the one `delta` places away (-1 up, +1 down), left-out columns and
 * column names and all. A column is identified by its members, so moving a layer changes the ORDER
 * of the keys that name it and not which column it is: the column a researcher left out, or named,
 * is still that column. It keeps its name as typed (a column called "Dallas - Exposed" is still
 * called that when exposure becomes layer 1); the columns that were never named take the new
 * layer order in their generated labels.
 */
export function moveLayer(draft: NestDraft, layerIdx: number, delta: -1 | 1): NestDraft {
    const to = layerIdx + delta;
    if (layerIdx < 0 || to < 0 || layerIdx >= draft.layers.length || to >= draft.layers.length) {
        return draft;
    }
    const swap = <T>(list: T[]): T[] => {
        const out = [...list];
        [out[layerIdx], out[to]] = [out[to], out[layerIdx]];
        return out;
    };
    // An entry that does not have one key per layer is already stale; it stays as it was.
    const reorder = (keys: string[]) =>
        keys.length === draft.layers.length ? swap(keys) : [...keys];
    return {
        layers: swap(draft.layers),
        dropped: draft.dropped.map(reorder),
        names: draft.names.map((n) => ({ keys: reorder(n.keys), label: n.label })),
    };
}

/**
 * Ticks or unticks members of a layer. `keys` names them; leave it out for every member (All / None).
 */
export function setMembersTicked(
    draft: NestDraft,
    layerIdx: number,
    keys: readonly string[] | null,
    ticked: boolean,
): NestDraft {
    const only = keys ? new Set(keys) : null;
    return withLayer(draft, layerIdx, (l) => ({
        ...l,
        options: l.options.map((o) =>
            (!only || only.has(o.member.key)) && o.ticked !== ticked ? { ...o, ticked } : o,
        ),
    }));
}

/** Renames one member, and with it every column it is in. A blank label is ignored. */
export function renameMember(
    draft: NestDraft,
    layerIdx: number,
    memberKey: string,
    label: string,
): NestDraft {
    const next = label.trim();
    if (!next) return draft;
    return withLayer(draft, layerIdx, (l) => ({
        ...l,
        options: l.options.map((o) =>
            o.member.key === memberKey && o.member.label !== next
                ? { ...o, member: { ...o.member, label: next } }
                : o,
        ),
    }));
}

/**
 * What `column` would be called if the researcher typed `typed` (M4S-5664): the text, trimmed, or
 * the generated label when that is blank or already is the generated label. The generated label is
 * what a column reads when it has no name, so clearing the field or typing it back UNDOES a name
 * and does not pin one: a column is only pinned (and stops following its members) by a name that
 * differs from what its members make.
 */
function labelFor(column: NestColumn, typed: string): { label: string; named: boolean } {
    const next = typed.trim();
    const named = next !== '' && next !== column.generated;
    return { label: named ? next : column.generated, named };
}

/**
 * Why `typed` cannot be the name of the column whose member keys are `keys`, or null when it can
 * (M4S-5664). The reason is NEST_DUPLICATE_COLUMN_NAME: another KEPT column already has that label,
 * generated or given, compared trimmed and without regard to case. Compute merges buckets that
 * share a label, so two columns with one name would count as one.
 *
 * A column that is left out is not compared (it is not in the nest), and a column is never
 * compared with itself. A blank name is fine: it puts the generated label back, and is refused only
 * when another column has taken that label in the meantime. A column that is not in the preview
 * (its member is unticked, or the nest is too wide to list) has nothing to refuse.
 */
export function columnNameError(
    draft: NestDraft,
    keys: readonly string[],
    typed: string,
): string | null {
    const columns = draftColumns(draft);
    const id = comboKey(keys);
    const column = columns.find((c) => comboKey(c.keys) === id);
    if (!column) return null;
    const wanted = labelFor(column, typed).label.trim().toLowerCase();
    const taken = columns.some(
        (c) => c !== column && !c.dropped && c.label.trim().toLowerCase() === wanted,
    );
    return taken ? NEST_DUPLICATE_COLUMN_NAME : null;
}

/**
 * Names one column of the nest in place of its generated label, or puts the generated label back
 * (M4S-5664). `keys` is the column's member keys in layer order, the key `dropped` uses.
 *
 *  - A blank name, or the text its members already make, is no name: the column follows its members
 *    again, so renaming a member renames it.
 *  - Any other name pins the column to it. A member rename no longer changes it; the columns that
 *    have no name of their own still follow their members.
 *  - A name that another kept column already has is refused: the draft comes back as it was
 *    (`columnNameError` says why, for the field to show). So is a column that is not in the preview.
 *  - Leaving the column out, or unticking one of its members, keeps the name in the draft, so
 *    putting it back restores it. A save prunes names whose columns no longer exist.
 *
 * Returns the very draft it was given when nothing changes.
 */
export function renameColumn(draft: NestDraft, keys: readonly string[], typed: string): NestDraft {
    const id = comboKey(keys);
    const column = draftColumns(draft).find((c) => comboKey(c.keys) === id);
    if (!column || columnNameError(draft, keys, typed) !== null) return draft;

    const { label, named } = labelFor(column, typed);
    const has = draft.names.some((n) => comboKey(n.keys) === id);
    if (named ? column.renamed && column.label === label : !has) return draft;

    // One entry per column: the first one for these keys is replaced (or dropped), any other goes.
    const names: NestColumnName[] = [];
    let placed = false;
    for (const n of draft.names) {
        if (comboKey(n.keys) !== id) names.push(n);
        else if (!placed) {
            placed = true;
            if (named) names.push({ keys: [...column.keys], label });
        }
    }
    if (named && !placed) names.push({ keys: [...column.keys], label });
    return { ...draft, names };
}

/**
 * Renames one column of a SAVED nest from outside the Nest tab (BT-2087: the table header, where a
 * custom column's name now lives in its variable). The buckets labelled `from` take `to`, and the
 * recipe names the column through `renameColumn`, so the Nest tab still reopens the variable
 * (`nestMatchesBuckets`). A variable whose recipe no longer generates its buckets only has its
 * buckets relabelled: its recipe is stale already and the tab opens it in Logic. Returns null when
 * the name is refused (another kept column has it), so the caller can say why.
 */
export function renameSavedNestColumn(
    cv: CustomVariable,
    from: string,
    to: string,
): CustomVariable | null {
    const buckets = cv.buckets.map((b) => (b.label === from ? { ...b, label: to } : b));
    if (!nestMatchesBuckets(cv)) return { ...cv, buckets };
    const recipe = parseNest(cv.nest);
    if (!recipe) return { ...cv, buckets };
    const column = combinations(recipe).find((c) => c.label === from);
    if (!column) return { ...cv, buckets };
    const draft = recipeToDraft(recipe, []);
    const named = renameColumn(draft, column.keys, to);
    if (named === draft) return null;
    const next: CustomVariable = { ...cv, buckets, nest: draftToRecipe(named) };
    return nestMatchesBuckets(next) ? next : null;
}

/** Leaves a column out of the nest, or puts it back. `keys` is the column's member keys in layer order. */
export function setColumnDropped(
    draft: NestDraft,
    keys: readonly string[],
    dropped: boolean,
): NestDraft {
    const id = comboKey([...keys]);
    const has = draft.dropped.some((k) => comboKey(k) === id);
    if (dropped === has) return draft;
    return {
        ...draft,
        dropped: dropped
            ? [...draft.dropped, [...keys]]
            : draft.dropped.filter((k) => comboKey(k) !== id),
    };
}

/** Moves one member up (-1) or down (+1) within its layer; the columns follow the new order. */
export function moveMember(
    draft: NestDraft,
    layerIdx: number,
    memberKey: string,
    delta: -1 | 1,
): NestDraft {
    if (layerIdx < 0 || layerIdx >= draft.layers.length) return draft;
    const layer = draft.layers[layerIdx];
    const from = layer.options.findIndex((o) => o.member.key === memberKey);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= layer.options.length) return draft;
    const options = [...layer.options];
    [options[from], options[to]] = [options[to], options[from]];
    return {
        ...draft,
        layers: draft.layers.map((l, i) => (i === layerIdx ? { ...l, options } : l)),
    };
}

// ── Merging answers inside a layer (M4S-5777, X32 / BT-2091) ─────────────────
//
// Two or more members of one layer become one member: its buckets are all of theirs, so it counts
// anyone in any of them (OR within a member, the rule a member always had), and every column it is
// in reads through `bucketsForCombination` like any other. The merged member's key names the
// members it covers (`merge:["Q1=1","Q1=2"]`), so a reopen (`recipeToDraft`) does not offer them
// again beside it. Nothing about how a nest is saved or counted changes: a merged member is an
// ordinary member with more buckets.

const MERGE_PREFIX = 'merge:';

/** The member keys a merged member covers, or [] for a member that is not merged. */
export function coveredKeys(key: string): string[] {
    if (!key.startsWith(MERGE_PREFIX)) return [];
    try {
        const parsed: unknown = JSON.parse(key.slice(MERGE_PREFIX.length));
        return isStringList(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/** True for a member made by merging others. */
export function isMergedMember(key: string): boolean {
    return coveredKeys(key).length > 0;
}

/** The key of a member that merges `keys` (merged members' own originals, flattened, sorted). */
export function mergedKey(keys: readonly string[]): string {
    const originals = new Set<string>();
    for (const k of keys) {
        const covered = coveredKeys(k);
        if (covered.length) covered.forEach((c) => originals.add(c));
        else originals.add(k);
    }
    return `${MERGE_PREFIX}${JSON.stringify([...originals].sort())}`;
}

/** The default label of a merged member: its members' labels joined with " + ". */
export function mergedLabel(labels: readonly string[]): string {
    return labels.join(' + ');
}

/** Why `keys` of layer `layerIdx` cannot be merged under `label`, or null when they can. */
export function mergeRefusal(
    draft: NestDraft,
    layerIdx: number,
    keys: readonly string[],
    label?: string,
): string | null {
    const layer = draft.layers[layerIdx];
    if (!layer) return 'There is no such layer.';
    const wanted = new Set(keys);
    const picked = layer.options.filter((o) => wanted.has(o.member.key));
    if (picked.length < 2) return 'Pick two or more answers to merge.';
    const buckets = picked.reduce((n, o) => n + o.member.buckets.length, 0);
    if (buckets > MEMBER_BUCKETS_MAX) {
        return `Merged, these answers would need ${buckets} conditions; one answer can hold ${MEMBER_BUCKETS_MAX}.`;
    }
    const name = (label ?? mergedLabel(picked.map((o) => o.member.label))).trim();
    if (!name) return 'Give the merged answer a name.';
    const clash = layer.options.some(
        (o) =>
            !wanted.has(o.member.key) && o.member.label.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clash) return 'Another answer in this layer already has this name.';
    return null;
}

/**
 * Merges members of one layer into one, placed where the first of them was, ticked if any of them
 * was. Its buckets are theirs (each relabelled), so it counts anyone any of them counted. Left-out
 * columns and column names that name a merged member are cleared: those columns are gone. Returns
 * the very draft it was given when `mergeRefusal` refuses.
 */
export function mergeMembers(
    draft: NestDraft,
    layerIdx: number,
    keys: readonly string[],
    label?: string,
): NestDraft {
    if (mergeRefusal(draft, layerIdx, keys, label) !== null) return draft;
    const layer = draft.layers[layerIdx];
    const wanted = new Set(keys);
    const picked = layer.options.filter((o) => wanted.has(o.member.key));
    const name = (label ?? mergedLabel(picked.map((o) => o.member.label))).trim();
    const merged: NestMember = {
        key: mergedKey(picked.map((o) => o.member.key)),
        label: name,
        buckets: picked.flatMap((o) =>
            o.member.buckets.map((b) => ({ ...copyMemberBucket(b), label: name })),
        ),
    };
    const first = layer.options.findIndex((o) => wanted.has(o.member.key));
    const options = layer.options.filter((o) => !wanted.has(o.member.key));
    options.splice(first, 0, { member: merged, ticked: picked.some((o) => o.ticked) });
    const namesMerged = (k: readonly string[]) => wanted.has(k[layerIdx]);
    return {
        layers: draft.layers.map((l, i) => (i === layerIdx ? { ...l, options } : l)),
        dropped: draft.dropped.filter((k) => !namesMerged(k)),
        names: draft.names.filter((n) => !namesMerged(n.keys)),
    };
}

/**
 * Takes a merged member apart again: the members it covers come back from the layer's source, in
 * its place, ticked as it was. Returns the very draft it was given when the member is not merged,
 * or when the source no longer offers every member it covers (it stays merged rather than lose
 * one).
 */
export function splitMember(
    draft: NestDraft,
    layerIdx: number,
    key: string,
    source: Pick<NestSource, 'members'> | undefined,
): NestDraft {
    const layer = draft.layers[layerIdx];
    const covered = coveredKeys(key);
    if (!layer || covered.length === 0 || !source) return draft;
    const at = layer.options.findIndex((o) => o.member.key === key);
    if (at < 0) return draft;
    const originals = covered.map((k) => source.members.find((m) => m.key === k));
    if (originals.some((m) => !m)) return draft;
    const present = new Set(layer.options.map((o) => o.member.key));
    const back = (originals as NestMember[])
        .filter((m) => !present.has(m.key))
        .map((m) => ({ member: copyMember(m), ticked: layer.options[at].ticked }));
    const options = [...layer.options];
    options.splice(at, 1, ...back);
    const namesIt = (k: readonly string[]) => k[layerIdx] === key;
    return {
        layers: draft.layers.map((l, i) => (i === layerIdx ? { ...l, options } : l)),
        dropped: draft.dropped.filter((k) => !namesIt(k)),
        names: draft.names.filter((n) => !namesIt(n.keys)),
    };
}

// ── Nesting by dropping one item on another (M4S-5777, X32) ──────────────────

/**
 * The Nest source a banner item stands for: a question by its id, Survey / Wave, a variable
 * (`cv:<id>`) or, for a variable under a header, its group (`group:<name>`), which is how the
 * picker offers it.
 */
export function nestSourceIdFor(
    bannerId: string,
    customVariables: readonly CustomVariable[],
): string {
    const cv = customVariables.find((v) => v.id === bannerId);
    if (!cv) return bannerId;
    const group = cv.cvGroupName?.trim();
    return group ? `group:${group}` : `cv:${cv.id}`;
}

/**
 * A Nest draft seeded with one layer per banner item, outermost first (dropping Gender onto Age
 * gives Gender outer and Age inner: Age within Gender). Each layer is checked against the ones
 * before it the way the picker checks it, so a refusal ("Already split by layer 1", "Matrix
 * questions can't be nested") comes back as a reason instead of a draft.
 */
export function seedNestDraft(
    catalog: NestCatalog,
    customVariables: CustomVariable[],
    bannerIds: readonly string[],
): { draft: NestDraft } | { refused: string } {
    let draft = emptyDraft();
    for (const id of bannerIds.slice(0, NEST_MAX_LAYERS)) {
        const sections = nestSources(catalog, customVariables, {
            layers: draft.layers,
            forLayer: draft.layers.length,
        });
        const sourceId = nestSourceIdFor(id, customVariables);
        const source = sections.flatMap((s) => s.sources).find((s) => s.id === sourceId);
        if (!source) return { refused: `${id} can't be nested.` };
        if (source.disabled) return { refused: `${source.label}: ${source.disabled}` };
        draft = addLayer(draft, source);
    }
    return { draft };
}

// ── Respondent counts (P1) ───────────────────────────────────────────────────
//
// How many respondents each member and each column would have, shown before Create. They come from
// the public `computeFromApi` itself, with no row questions: the draft goes in as banner
// variables and each column's `base` is read back. So the numbers cannot disagree with the table,
// and nothing new is exported from compute. The caller supplies the compute (it owns the data and
// the live filters); this module only builds what to compute and reads the answer.

/** Ids of the variables a count is computed from. They start `cv_nest_`, so each is its own header. */
export const NEST_COUNT_ID_PREFIX = `${NEST_ID_PREFIX}count_`;

/** A column's key in `NestCounts.columns`: its member keys in layer order, joined as its label is. */
export function columnCountKey(keys: readonly string[]): string {
    return keys.join(NEST_LABEL_SEPARATOR);
}

export interface NestCounts {
    /** Respondents per column (left-out ones too), by `columnCountKey`. */
    columns: Record<string, number>;
    /** Respondents per member, one map per layer, by member key. */
    members: Record<string, number>[];
}

/**
 * The variables to compute a draft's counts from, as banner variables with no rows:
 *  - `cv_nest_count_cols`: every combination of the TICKED members, none left out, each column
 *    labelled by its member keys. Omitted above NEST_MAX_COLUMNS (or with a layer missing), where
 *    the tab shows no list.
 *  - `cv_nest_count_l<i>`: one column per member of layer `i`, ticked or not, labelled by its key.
 *    A member with several buckets is one column (same label), counting anyone in any of them.
 */
export function nestCountVariables(draft: NestDraft): CustomVariable[] {
    const variable = (id: string, buckets: VariableBucket[]): CustomVariable => ({
        id: `${NEST_COUNT_ID_PREFIX}${id}`,
        name: id,
        kind: 'logic',
        buckets,
        sourceQuestionId: '',
        ranges: [],
        otherLabel: 'Other',
        placement: 'banner',
        // M4S-5777: counted under the rules the nest it previews is saved with.
        rules: CONDITION_RULES,
    });
    const out: CustomVariable[] = [];
    const columns = draftColumnCount(draft);
    if (draft.layers.length >= 2 && columns >= 1 && columns <= NEST_MAX_COLUMNS) {
        const recipe: NestRecipe = {
            version: 1,
            layers: draft.layers.map((l) => ({
                source: l.source,
                label: l.label,
                members: l.options
                    .filter((o) => o.ticked)
                    .map((o) => ({ ...copyMember(o.member), label: o.member.key })),
            })),
        };
        out.push(variable('cols', nestToBuckets(recipe)));
    }
    draft.layers.forEach((l, i) => {
        out.push(
            variable(
                `l${i}`,
                l.options.flatMap((o) =>
                    o.member.buckets.map((b) => ({ ...copyMemberBucket(b), label: o.member.key })),
                ),
            ),
        );
    });
    return out;
}

/**
 * What a count may cost, in bucket evaluations (buckets x respondents). compute evaluates every
 * bucket for every respondent on the main thread: measured at about 0.4 to 0.9 microseconds each,
 * so 200 columns at 6,000 respondents is about a second, and at 20,000 nearly four. A million is
 * about half a second at worst.
 */
export const NEST_COUNT_BUDGET = 1_000_000;

/**
 * The count variables that fit the budget for `respondents`. The columns go first, because they
 * cost the most (every combination) and the members' counts answer the question that matters when
 * the nest is wide: how many respondents each answer has. If even the members do not fit, nothing
 * is counted. `readNestCounts` treats a column that was not computed as unknown.
 */
export function nestCountWithinBudget(
    variables: CustomVariable[],
    respondents: number,
): CustomVariable[] {
    const cost = (vs: CustomVariable[]) =>
        vs.reduce((n, v) => n + v.buckets.length, 0) * Math.max(respondents, 0);
    if (cost(variables) <= NEST_COUNT_BUDGET) return variables;
    const members = variables.filter((v) => v.id !== `${NEST_COUNT_ID_PREFIX}cols`);
    return cost(members) <= NEST_COUNT_BUDGET ? members : [];
}

/**
 * `nestCountVariables(draft)` computed as banner columns: `base` maps a column id to its
 * respondents. A column that is not in `base` is unknown, not zero, and is left out of the result.
 * Above NEST_MAX_COLUMNS no column was computed, so none is looked up: walking every combination
 * of three big layers (a million) to find nothing froze the dialog on each tick (M4S-5605).
 */
export function readNestCounts(draft: NestDraft, base: Record<string, number>): NestCounts {
    const read = (variableId: string, label: string): number | undefined => {
        const n = base[`${NEST_COUNT_ID_PREFIX}${variableId}:${label}`];
        return typeof n === 'number' ? n : undefined;
    };
    const columns: Record<string, number> = {};
    const ticked = draft.layers.map((l) => l.options.filter((o) => o.ticked).map((o) => o.member));
    if (
        draft.layers.length >= 2 &&
        draftColumnCount(draft) <= NEST_MAX_COLUMNS &&
        ticked.every((t) => t.length > 0)
    ) {
        for (const c of crossLayers(ticked)) {
            const key = columnCountKey(c.keys);
            const n = read('cols', key);
            if (n !== undefined) columns[key] = n;
        }
    }
    const members = draft.layers.map((l, i) => {
        const out: Record<string, number> = {};
        for (const o of l.options) {
            const n = read(`l${i}`, o.member.key);
            if (n !== undefined) out[o.member.key] = n;
        }
        return out;
    });
    return { columns, members };
}

/** The columns still kept whose count is known to be zero, as member keys in layer order. */
export function columnsWithNoRespondents(draft: NestDraft, counts: NestCounts): string[][] {
    return draftColumns(draft)
        .filter((c) => !c.dropped && counts.columns[columnCountKey(c.keys)] === 0)
        .map((c) => c.keys);
}

/** Leaves each of these columns out of the nest. */
export function leaveOutColumns(draft: NestDraft, columns: readonly string[][]): NestDraft {
    return columns.reduce((d, keys) => setColumnDropped(d, keys, true), draft);
}

// ── The Logic tab's nudge (P1) ───────────────────────────────────────────────

/**
 * True when a Logic variable is being built the long way: two or more buckets that each join
 * conditions on the same two or more questions with an AND, which is a cross of those questions
 * one pair at a time. That is what Nest does in one pass. It only reads the buckets, and says
 * nothing about whether they are right.
 */
export function looksLikeCrossing(buckets: VariableBucket[]): boolean {
    const seen = new Map<string, number>();
    for (const b of buckets) {
        const questions = new Set(
            b.conditions
                .filter((c) => c.questionId.trim() && c.value.trim())
                .map((c) => c.questionId.trim().toUpperCase()),
        );
        if (questions.size < 2 || !bucketConnectors(b).some((c) => c === 'all')) continue;
        const shape = [...questions].sort().join('|');
        seen.set(shape, (seen.get(shape) ?? 0) + 1);
    }
    return [...seen.values()].some((n) => n >= 2);
}

// ── Overlap and blockers ─────────────────────────────────────────────────────

/** A member's codes when it is `eq` conditions on ONE question, all joined by OR; else null. */
function eqCodeSet(m: NestMember): { qid: string; codes: Set<string> } | null {
    let qid: string | null = null;
    const codes = new Set<string>();
    for (const b of m.buckets) {
        // A grouped bucket (X46) is not a plain OR of answers, so it proves nothing.
        if (isGrouped(b)) return null;
        if (bucketConnectors(b).some((c) => c !== 'any')) return null;
        for (const c of b.conditions) {
            if (c.operator !== 'eq' && c.operator !== 'in') return null;
            const q = c.questionId.toUpperCase();
            if (qid !== null && q !== qid) return null;
            qid = q;
            const picked = c.operator === 'in' ? valuesOf(c) : [c.value];
            for (const v of picked) codes.add(v.trim().toUpperCase());
        }
    }
    return qid === null ? null : { qid, codes };
}

/**
 * True only when two members are `eq` conditions on ONE question joined by OR and their code sets
 * intersect (BT-2019's `netsOverlap` rule): a respondent provably holds both. Two answers of a
 * question, two surveys, and members built on several questions or with `neq` or numeric
 * operators all return false. That includes two answers of a MULTI-select question, whose
 * respondents can overlap, exactly as they do when that question is on the banner: the catalog
 * carries no select-mode flag, so they are noted and never blocked.
 */
export function membersProvablyShare(a: NestMember, b: NestMember): boolean {
    const sa = eqCodeSet(a);
    const sb = eqCodeSet(b);
    if (!sa || !sb || sa.qid !== sb.qid) return false;
    for (const code of sa.codes) if (sb.codes.has(code)) return true;
    return false;
}

/** True when a layer's members read more than one question, so they can share respondents. */
export function layerMayShare(layer: NestLayer | NestDraft['layers'][number]): boolean {
    return columnsOfMembers(membersOfLayer(layer)).length > 1;
}

export type NestPlacement = 'banner' | 'row';

/** The reason a nest of `count` columns cannot be saved. The tab and the footer say it alike. */
export function nestTooWide(count: number): string {
    return `This nest makes ${count} columns and the most is ${NEST_MAX_COLUMNS}. Untick some answers or remove a layer`;
}

/**
 * The single reason Create cannot save the draft, worded by layer and member names, or null when
 * it can. Works on the draft, so it can say "add a second layer" and "nothing ticked". It is the
 * save-time guard for the rule the source picker applies as you pick.
 */
export function nestBlocker(args: {
    name: string;
    draft: NestDraft;
    placement: NestPlacement;
}): string | null {
    const { name, draft, placement } = args;
    const banner = placement === 'banner';
    if (!name.trim()) return banner ? 'Enter a column header' : 'Enter a variable name';
    if (draft.layers.length === 0) return 'Pick what to split by in layer 1';
    if (draft.layers.length < 2) return 'Add layer 2 to cross with layer 1';

    for (let i = 0; i < draft.layers.length; i++) {
        if (!draft.layers[i].options.some((o) => o.ticked)) {
            return `Layer ${i + 1} (${draft.layers[i].label}) has nothing ticked`;
        }
    }

    const columns = draft.layers.map((l) => new Set(columnsOfMembers(membersOfLayer(l))));
    for (let i = 0; i < draft.layers.length; i++) {
        for (let j = i + 1; j < draft.layers.length; j++) {
            if ([...columns[i]].some((c) => columns[j].has(c))) {
                return `Layer ${i + 1} (${draft.layers[i].label}) and layer ${j + 1} (${draft.layers[j].label}) split by the same question; a respondent has one answer, so those columns would be empty`;
            }
        }
    }

    const count = draftColumnCount(draft);
    if (count > NEST_MAX_COLUMNS) return nestTooWide(count);

    const seen = new Set<string>();
    for (const col of draftColumns(draft)) {
        if (col.dropped) continue;
        const label = col.label.trim().toLowerCase();
        if (seen.has(label)) {
            // A column's own name counts, so the way out is a member's name or a column's.
            return `Two columns are both called "${col.label}". Rename a member or a column so every column has its own name`;
        }
        seen.add(label);
    }

    if (banner) {
        for (const layer of draft.layers) {
            const ticked = layer.options.filter((o) => o.ticked).map((o) => o.member);
            for (let i = 0; i < ticked.length; i++) {
                for (let j = i + 1; j < ticked.length; j++) {
                    if (membersProvablyShare(ticked[i], ticked[j])) {
                        return `${ticked[i].label} and ${ticked[j].label} share answers, so their columns share respondents and can't be tested against each other. Untick one, or place this nest on Rows.`;
                    }
                }
            }
        }
    }
    return null;
}

// ── Naming and the saved variable ────────────────────────────────────────────

/**
 * The header a nest starts with: layer 1's label (the base and Juhi's workbook name a header for
 * its first split), then `<L1> × <L2>` when a header of that name already exists (an exact match
 * would join and be tested with that header), then " (2)", " (3)"…
 */
export function defaultNestName(layers: { label: string }[], existingHeaders: string[]): string {
    if (layers.length === 0) return '';
    const taken = new Set(existingHeaders.map((h) => h.trim().toLowerCase()));
    const isTaken = (name: string) => taken.has(name.trim().toLowerCase());
    const first = layers[0].label.trim();
    if (!isTaken(first)) return first;
    const base = layers.length > 1 ? `${first}${NEST_NAME_JOINER}${layers[1].label.trim()}` : first;
    if (layers.length > 1 && !isTaken(base)) return base;
    let n = 2;
    while (isTaken(`${base} (${n})`)) n++;
    return `${base} (${n})`;
}

/**
 * The headers a variable saved from the dialog can join, read from the banner of the last Run:
 * every header but "Total", leaving out one that only the variable being edited makes. Compute
 * drops that variable's columns and adds them back, so its own header is not there to join
 * (custom columns are `<variable id>:<label>`). A header it shares with the base or another
 * variable is still joinable.
 */
export function joinableHeaders(
    banner: { group: string; cols: { id: string }[] }[],
    editingId?: string,
): string[] {
    const own = editingId ? `${editingId}:` : null;
    return banner
        .filter((g) => g.group !== 'Total')
        .filter((g) => !own || g.cols.length === 0 || g.cols.some((c) => !c.id.startsWith(own)))
        .map((g) => g.group);
}

/**
 * What a column counts, as one sentence for its tooltip: each member's buckets read through
 * `describeBucket`, members joined by AND. A member of one plain condition reads plain; anything
 * longer is parenthesised.
 */
export function describeColumn(
    members: NestMember[],
    labelOf: (condition: VariableCondition) => string,
): string {
    const parts = members.map((m) => {
        const buckets = m.buckets.map((b) => describeBucket(b, labelOf));
        const text = buckets.join(' OR ');
        const plain = buckets.length === 1 && m.buckets[0].conditions.length === 1;
        return plain ? text : `(${text})`;
    });
    return parts.join(' AND ');
}

/**
 * The ordinary variable a nest saves as. Its id is `cv_nest_<ms>` unless one is given (an edit
 * keeps its id, and with it the banner renames keyed on it). It never starts `cv_bl_`, so the iOOH
 * base merge leaves it alone. `nest` carries the recipe; `buckets` are what compute reads.
 */
export function nestedVariable(args: {
    id?: string;
    name: string;
    recipe: NestRecipe;
    placement: NestPlacement;
    cvGroupName?: string;
    otherLabel?: string;
}): CustomVariable {
    const group = args.cvGroupName?.trim();
    return {
        id: args.id ?? `${NEST_ID_PREFIX}${Date.now()}`,
        name: args.name.trim(),
        kind: 'logic',
        buckets: nestToBuckets(args.recipe),
        sourceQuestionId: '',
        ranges: [],
        otherLabel: args.otherLabel?.trim() || 'Uncategorized',
        placement: args.placement,
        ...(group ? { cvGroupName: group } : {}),
        nest: args.recipe,
        // M4S-5777: built under today's condition rules (conditions.ts).
        rules: CONDITION_RULES,
    };
}
